import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { AppDispatch, RootState } from '../store/store';
import { QUICK_FILTER_KEYS } from 'components/LeadManagement/constants';
import {
  fetchReferrals,
  fetchReferralsDropdownData,
  setFilters as setReferralsFilters,
  setPage as setReferralsPage,
  setPageSize as setReferralsPageSize,
  setSorting as setReferralsSorting,
} from '../features/lma/referralsSlice';
import {
  fetchNonReferrals,
  fetchNonReferralsDropdownData,
  setFilters as setNonReferralsFilters,
  setPage as setNonReferralsPage,
  setPageSize as setNonReferralsPageSize,
  setSorting as setNonReferralsSorting,
} from '../features/lma/nonReferralsSlice';
import {
  fetchProcessedReferrals,
  fetchProcessedReferralsDropdownData,
  setProcessedDataViewAndRefetch,
  setFilters as setProcessedReferralsFilters,
  setPage as setProcessedReferralsPage,
  setPageSize as setProcessedReferralsPageSize,
  setSorting as setProcessedReferralsSorting,
} from '../features/lma/processedReferralsSlice';
import {
  fetchFlaggedRecords,
  fetchFlaggedRecordsDropdownData,
  setFilters as setFlaggedRecordsFilters,
  setPage as setFlaggedRecordsPage,
  setPageSize as setFlaggedRecordsPageSize,
  setSorting as setFlaggedRecordsSorting,
} from '../features/lma/flaggedRecordsSlice';
import type { FetchReferralDataPayload } from '../services/leadManagementApi';

// Build lead status filter params from Sitecore config
const buildLeadStatusParams = (leadStatusFilters: any[]): Record<string, string> => {
  if (!leadStatusFilters || !Array.isArray(leadStatusFilters)) {
    return {};
  }

  const params: Record<string, string> = {};

  leadStatusFilters.forEach((filter: any) => {
    const name = filter.name?.toLowerCase(); // "hot", "warm", "cold"
    const lowerLimit = filter.fields?.LSFDaysLowerLimit?.value;
    const upperLimit = filter.fields?.LSFDaysUpperLimit?.value;

    if (name && lowerLimit !== undefined) {
      // Use static keys for each lead type
      if (name === 'hot' && upperLimit !== undefined) {
        params['hotLead'] = `${lowerLimit}-${upperLimit}`;
      } else if (name === 'warm' && upperLimit !== undefined) {
        params['warmLead'] = `${lowerLimit}-${upperLimit}`;
      } else if (name === 'cold') {
        // Cold lead only uses lower limit
        params['coldLead'] = lowerLimit;
      }
    }
  });

  return params;
};

export const useLMAReferrals = (leadStatusFilters: any[]) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
  } = useSelector((state: RootState) => state.lmaReferrals);

  const loadReferrals = useCallback(
    (customFilters?: Record<string, string>, page?: number, size?: number) => {
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters: {
          ...customFilters,
          leadStatus: 'Requested',
        },
        sortBy,
        sortOrder,
      };

      const pageNo = page ?? 1;
      const params = { PageNo: pageNo, PageSize: size || pageSize };
      dispatch(setReferralsPage(pageNo));
      return dispatch(fetchReferrals({ params, payload })).unwrap();
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize, sortBy, sortOrder]
  );

  const applyFilters = useCallback(
    (newFilters: Record<string, string>) => {
      // leadIds inside filters; check quickFilters for "lockedByMe". When not selected, pass leadIds as null (clear any cached value)
      const filtersForPayload = { ...newFilters };
      const quickFiltersValue = newFilters.quickFilters;
      const hasLockedByMe = Array.isArray(quickFiltersValue)
        ? quickFiltersValue.includes(QUICK_FILTER_KEYS.LOCKED_BY_ME)
        : String(quickFiltersValue ?? '')
            .split(',')
            .map((s: string) => s.trim())
            .includes(QUICK_FILTER_KEYS.LOCKED_BY_ME);
      if (!hasLockedByMe) {
        filtersForPayload.leadIds = null as unknown as string;
      }
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters: filtersForPayload,
        sortBy,
        sortOrder,
      };

      // Update filters in Redux state synchronously after API call (convert to strings)
      dispatch(setReferralsFilters(newFilters));
      dispatch(setReferralsPage(1)); // Also update page to 1

      const params = { PageNo: 1, PageSize: pageSize };
      // Then dispatch the API call
      dispatch(fetchReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, pageSize, sortBy, sortOrder]
  );

  const changePage = useCallback(
    (page: number) => {
      dispatch(setReferralsPage(page));
      // Automatically fetch with new page
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters,
        sortBy,
        sortOrder,
      };

      const params = { PageNo: page, PageSize: pageSize };
      // Then dispatch the API call
      dispatch(fetchReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, pageSize, sortBy, sortOrder]
  );

  const changePageSize = useCallback(
    (size: number) => {
      dispatch(setReferralsPageSize(size));
      // Automatically fetch with new page size
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters,
        sortBy,
        sortOrder,
      };

      const params = { PageNo: 1, PageSize: size };
      // Then dispatch the API call
      dispatch(fetchReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, sortBy, sortOrder]
  );

  const changeSorting = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      dispatch(setReferralsSorting({ sortBy: field, sortOrder: order }));
      dispatch(setReferralsPage(1)); // Reset page to 1 when sorting changes
      // Automatically fetch with new sorting
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters,
        sortBy: field,
        sortOrder: order,
      };

      const params = { PageNo: 1, PageSize: pageSize };
      dispatch(fetchReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize]
  );

  const clearSorting = useCallback(() => {
    dispatch(setReferralsSorting({ sortBy: undefined, sortOrder: undefined }));
    dispatch(setReferralsPage(1));
    const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
    const payload: FetchReferralDataPayload = {
      configuration: { leads: leadStatusParams },
      filters,
    };
    const params = { PageNo: 1, PageSize: pageSize };
    dispatch(fetchReferrals({ params, payload }));
  }, [dispatch, leadStatusFilters, filters, pageSize]);

  const loadDropdownData = useCallback(() => {
    dispatch(fetchReferralsDropdownData(leadStatusFilters));
  }, [dispatch, leadStatusFilters]);

  const clearFilters = useCallback(() => {
    dispatch(setReferralsFilters({}));
  }, [dispatch]);

  return {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
    loadReferrals,
    loadDropdownData,
    applyFilters,
    changePage,
    changePageSize,
    changeSorting,
    clearSorting,
    clearFilters,
  };
};

export const useLMANonReferrals = (leadStatusFilters: any[]) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
  } = useSelector((state: RootState) => state.lmaNonReferrals);

  const loadNonReferrals = useCallback(
    (customFilters?: Record<string, string>, page?: number, size?: number) => {
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const pageNo = page ?? 1;
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters: customFilters,
        sortBy,
        sortOrder,
      };
      dispatch(setNonReferralsPage(pageNo));
      const params = { PageNo: pageNo, PageSize: size || pageSize };
      return dispatch(fetchNonReferrals({ params, payload })).unwrap();
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize, sortBy, sortOrder]
  );

  const applyFilters = useCallback(
    (newFilters: Record<string, string>) => {
      // leadIds inside filters; check quickFilters for "lockedByMe". When not selected, pass leadIds as null
      const filtersForPayload = { ...newFilters };
      const quickFiltersValue = newFilters.quickFilters;
      const hasLockedByMe = Array.isArray(quickFiltersValue)
        ? quickFiltersValue.includes(QUICK_FILTER_KEYS.LOCKED_BY_ME)
        : String(quickFiltersValue ?? '')
            .split(',')
            .map((s: string) => s.trim())
            .includes(QUICK_FILTER_KEYS.LOCKED_BY_ME);
      if (!hasLockedByMe) {
        filtersForPayload.leadIds = null as unknown as string;
      }
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters: filtersForPayload,
        sortBy,
        sortOrder,
      };

      dispatch(setNonReferralsFilters(newFilters));
      dispatch(setNonReferralsPage(1));
      const params = { PageNo: 1, PageSize: pageSize };
      dispatch(fetchNonReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, pageSize, sortBy, sortOrder]
  );

  const changePage = useCallback(
    (page: number) => {
      dispatch(setNonReferralsPage(page));
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters,
        sortBy,
        sortOrder,
      };
      const params = { PageNo: page, PageSize: pageSize };
      dispatch(fetchNonReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, pageSize, sortBy, sortOrder]
  );

  const changePageSize = useCallback(
    (size: number) => {
      dispatch(setNonReferralsPageSize(size));
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters,
        sortBy,
        sortOrder,
      };
      const params = { PageNo: 1, PageSize: size };
      dispatch(fetchNonReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, sortBy, sortOrder]
  );

  const changeSorting = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      dispatch(setNonReferralsSorting({ sortBy: field, sortOrder: order }));
      dispatch(setNonReferralsPage(1));
      // Automatically fetch with new sorting
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters,
        sortBy: field,
        sortOrder: order,
      };
      const params = { PageNo: 1, PageSize: pageSize };
      dispatch(fetchNonReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize]
  );

  const clearSorting = useCallback(() => {
    dispatch(setNonReferralsSorting({ sortBy: undefined, sortOrder: undefined }));
    dispatch(setNonReferralsPage(1));
    const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
    const payload: FetchReferralDataPayload = {
      configuration: {
        leads: leadStatusParams,
      },
      filters,
    };
    const params = { PageNo: 1, PageSize: pageSize };
    dispatch(fetchNonReferrals({ params, payload }));
  }, [dispatch, leadStatusFilters, filters, pageSize]);

  const loadDropdownData = useCallback(() => {
    dispatch(fetchNonReferralsDropdownData(leadStatusFilters));
  }, [dispatch, leadStatusFilters]);

  const clearFilters = useCallback(() => {
    dispatch(setNonReferralsFilters({}));
  }, [dispatch]);

  return {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
    loadNonReferrals,
    loadDropdownData,
    applyFilters,
    changePage,
    changePageSize,
    changeSorting,
    clearSorting,
    clearFilters,
  };
};

export const useLMAProcessedReferrals = (leadStatusFilters: any[]) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
    useFullProfileProcessedData,
  } = useSelector((state: RootState) => state.lmaProcessedReferrals);

  const switchProcessedDataView = useCallback(
    (useFullProfile: boolean) => dispatch(setProcessedDataViewAndRefetch(useFullProfile)),
    [dispatch]
  );

  const loadProcessedReferrals = useCallback(
    (customFilters?: Record<string, string>, page?: number, size?: number) => {
      const payload: FetchReferralDataPayload = {
        filters: customFilters,
        sortBy,
        sortOrder,
      };

      const pageNo = page ?? 1;
      const params = { PageNo: pageNo, PageSize: size || pageSize };
      dispatch(setProcessedReferralsPage(pageNo));
      return dispatch(fetchProcessedReferrals({ params, payload })).unwrap();
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize, sortBy, sortOrder]
  );

  const applyFilters = useCallback(
    (newFilters: Record<string, string>) => {
      const payload: FetchReferralDataPayload = {
        filters: newFilters,
        sortBy,
        sortOrder,
      };

      dispatch(setProcessedReferralsFilters(newFilters));
      dispatch(setProcessedReferralsPage(1));

      const params = { PageNo: 1, PageSize: pageSize };
      dispatch(fetchProcessedReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, pageSize, sortBy, sortOrder]
  );

  const changePage = useCallback(
    (page: number) => {
      dispatch(setProcessedReferralsPage(page));
      const payload: FetchReferralDataPayload = {
        filters,
        sortBy,
        sortOrder,
      };

      const params = { PageNo: page, PageSize: pageSize };
      dispatch(fetchProcessedReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, pageSize, sortBy, sortOrder]
  );

  const changePageSize = useCallback(
    (size: number) => {
      dispatch(setProcessedReferralsPageSize(size));
      const payload: FetchReferralDataPayload = {
        filters,
        sortBy,
        sortOrder,
      };

      const params = { PageNo: 1, PageSize: size };
      dispatch(fetchProcessedReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, sortBy, sortOrder]
  );

  const changeSorting = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      dispatch(setProcessedReferralsSorting({ sortBy: field, sortOrder: order }));
      dispatch(setProcessedReferralsPage(1));
      const payload: FetchReferralDataPayload = {
        filters,
        sortBy: field,
        sortOrder: order,
      };

      const params = { PageNo: 1, PageSize: pageSize };
      dispatch(fetchProcessedReferrals({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize]
  );

  const clearSorting = useCallback(() => {
    dispatch(setProcessedReferralsSorting({ sortBy: undefined, sortOrder: undefined }));
    dispatch(setProcessedReferralsPage(1));
    const payload: FetchReferralDataPayload = {
      filters,
    };
    const params = { PageNo: 1, PageSize: pageSize };
    dispatch(fetchProcessedReferrals({ params, payload }));
  }, [dispatch, filters, pageSize]);

  const loadDropdownData = useCallback(() => {
    dispatch(fetchProcessedReferralsDropdownData(leadStatusFilters));
  }, [dispatch, leadStatusFilters]);

  const clearFilters = useCallback(() => {
    dispatch(setProcessedReferralsFilters({}));
  }, [dispatch]);

  return {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
    useFullProfileProcessedData,
    switchProcessedDataView,
    loadProcessedReferrals,
    loadDropdownData,
    applyFilters,
    changePage,
    changePageSize,
    changeSorting,
    clearSorting,
    clearFilters,
  };
};

export const useLMAFlaggedRecords = (leadStatusFilters: any[]) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
  } = useSelector((state: RootState) => state.lmaFlaggedRecords);

  const loadFlaggedRecords = useCallback(
    (customFilters?: Record<string, string>, page?: number, size?: number) => {
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters: customFilters,
        sortBy,
        sortOrder,
      };

      const pageNo = page ?? 1;
      const params = { PageNo: pageNo, PageSize: size || pageSize };
      dispatch(setFlaggedRecordsPage(pageNo));
      return dispatch(fetchFlaggedRecords({ params, payload })).unwrap();
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize, sortBy, sortOrder]
  );

  const applyFilters = useCallback(
    (newFilters: Record<string, string>) => {
      const filtersForPayload = { ...newFilters };
      const quickFiltersValue = newFilters.quickFilters;
      const hasLockedByMe = Array.isArray(quickFiltersValue)
        ? quickFiltersValue.includes(QUICK_FILTER_KEYS.LOCKED_BY_ME)
        : String(quickFiltersValue ?? '')
            .split(',')
            .map((s: string) => s.trim())
            .includes(QUICK_FILTER_KEYS.LOCKED_BY_ME);
      if (!hasLockedByMe) {
        filtersForPayload.leadIds = null as unknown as string;
      }
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters: filtersForPayload,
        sortBy,
        sortOrder,
      };

      dispatch(setFlaggedRecordsFilters(newFilters));
      dispatch(setFlaggedRecordsPage(1));
      const params = { PageNo: 1, PageSize: pageSize };
      dispatch(fetchFlaggedRecords({ params, payload }));
    },
    [dispatch, leadStatusFilters, pageSize, sortBy, sortOrder]
  );

  const changePage = useCallback(
    (page: number) => {
      dispatch(setFlaggedRecordsPage(page));
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters,
        sortBy,
        sortOrder,
      };
      const params = { PageNo: page, PageSize: pageSize };
      dispatch(fetchFlaggedRecords({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, pageSize, sortBy, sortOrder]
  );

  const changePageSize = useCallback(
    (size: number) => {
      dispatch(setFlaggedRecordsPageSize(size));
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters,
        sortBy,
        sortOrder,
      };
      const params = { PageNo: 1, PageSize: size };
      dispatch(fetchFlaggedRecords({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, sortBy, sortOrder]
  );

  const changeSorting = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      dispatch(setFlaggedRecordsSorting({ sortBy: field, sortOrder: order }));
      dispatch(setFlaggedRecordsPage(1));
      const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
      const payload: FetchReferralDataPayload = {
        configuration: {
          leads: leadStatusParams,
        },
        filters,
        sortBy: field,
        sortOrder: order,
      };
      const params = { PageNo: 1, PageSize: pageSize };
      dispatch(fetchFlaggedRecords({ params, payload }));
    },
    [dispatch, leadStatusFilters, filters, currentPage, pageSize]
  );

  const clearSorting = useCallback(() => {
    dispatch(setFlaggedRecordsSorting({ sortBy: undefined, sortOrder: undefined }));
    dispatch(setFlaggedRecordsPage(1));
    const leadStatusParams = buildLeadStatusParams(leadStatusFilters);
    const payload: FetchReferralDataPayload = {
      configuration: {
        leads: leadStatusParams,
      },
      filters,
    };
    const params = { PageNo: 1, PageSize: pageSize };
    dispatch(fetchFlaggedRecords({ params, payload }));
  }, [dispatch, leadStatusFilters, filters, pageSize]);

  const loadDropdownData = useCallback(() => {
    dispatch(fetchFlaggedRecordsDropdownData(leadStatusFilters));
  }, [dispatch, leadStatusFilters]);

  const clearFilters = useCallback(() => {
    dispatch(setFlaggedRecordsFilters({}));
  }, [dispatch]);

  return {
    data,
    totalRecords,
    loading,
    error,
    lastUpdated,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    dropdownData,
    dropdownLoading,
    dropdownError,
    loadFlaggedRecords,
    loadDropdownData,
    applyFilters,
    changePage,
    changePageSize,
    changeSorting,
    clearSorting,
    clearFilters,
  };
};
